import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { AuthContext } from '../../../contexts/AuthContext';
import type { ProspectSource, ProspectSourcesResponse } from '../../../types/adminRosterUploads';
import { filesLabel, formatInstant, sourceLabel } from '../roster-uploads/rosterUploadFormat';
import {
  batchExtractionHref,
  importerLabel,
  MANUAL_ENTRY_SOURCE_KIND,
  outcomePresentation,
  rosterUploadHref,
} from './prospectProvenanceFormat';
import { useProspectSources } from './useProspectSources';
import './ProspectCameFrom.css';

const SourceTitle = ({ source }: { source: ProspectSource }) => {
  if (source.sourceKind === MANUAL_ENTRY_SOURCE_KIND) {
    return <span className="prospect-came-from__title">Manual entry</span>;
  }

  if (source.sourceKind === 'BatchDistribution') {
    if (!source.batchExtractionId) {
      return <span className="prospect-came-from__title">Batch extraction (deleted)</span>;
    }
    return (
      <Link className="prospect-came-from__link" to={batchExtractionHref(source.batchExtractionId)}>
        {source.batchName ? `Batch extraction: ${source.batchName}` : 'Batch extraction'}
      </Link>
    );
  }

  if (source.rosterExtractionId) {
    const upload = source.rosterExtraction;
    return (
      <Link className="prospect-came-from__link" to={rosterUploadHref(source.rosterExtractionId)}>
        Roster upload
        {upload && (
          <span className="prospect-came-from__link-detail">
            {' '}
            ({sourceLabel(upload.sourceType)}, {filesLabel(upload.fileCount)})
          </span>
        )}
      </Link>
    );
  }

  return <span className="prospect-came-from__title">Roster import (upload not recorded)</span>;
};

const SourceItem = ({ source }: { source: ProspectSource }) => {
  const { label, tone } = outcomePresentation(source.outcome);
  const details = [
    source.teamName ? `Team: ${source.teamName}` : null,
    `By ${importerLabel(source.importedBy)}`,
  ].filter((part): part is string => part !== null);

  return (
    <li className="prospect-came-from__item" data-testid={`prospect-source-${source.id}`}>
      <div className="prospect-came-from__heading">
        <SourceTitle source={source} />
        <span className={`prospect-came-from__outcome prospect-came-from__outcome--${tone}`}>
          {label}
        </span>
      </div>
      <p className="prospect-came-from__details">
        {details.join(' · ')} ·{' '}
        <time dateTime={source.importedAt}>{formatInstant(source.importedAt)}</time>
      </p>
    </li>
  );
};

/**
 * No source row was ever written, so the record's own creation stamp is all there is to show:
 * "Record created <date> by <name or email>", leaving out whichever half is unknown.
 */
const RecordCreatedFallback = ({ data }: { data: ProspectSourcesResponse }) => {
  const { prospectCreatedAt, prospectCreatedBy } = data;
  return (
    <div className="prospect-came-from__fallback" data-testid="prospect-sources-empty">
      <p className="prospect-came-from__created">
        Record created
        {prospectCreatedAt && (
          <>
            {' '}
            <time dateTime={prospectCreatedAt}>{formatInstant(prospectCreatedAt)}</time>
          </>
        )}
        {prospectCreatedBy && ` by ${importerLabel(prospectCreatedBy)}`}
      </p>
      <p className="prospect-came-from__message">
        It predates import tracking or has no recorded source.
      </p>
    </div>
  );
};

const ProspectSourcesList = ({ prospectId }: { prospectId: string }) => {
  const { state, reload } = useProspectSources(prospectId);

  if (state.kind === 'loading') {
    return (
      <p className="prospect-came-from__message" role="status">
        Loading import history...
      </p>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="prospect-came-from__error" role="alert">
        <span>{state.message}</span>
        <Button type="button" variant="secondary" onClick={reload}>
          Try again
        </Button>
      </div>
    );
  }

  if (state.data.sources.length === 0) {
    return <RecordCreatedFallback data={state.data} />;
  }

  return (
    <ul className="prospect-came-from__list">
      {state.data.sources.map((source) => (
        <SourceItem key={source.id} source={source} />
      ))}
    </ul>
  );
};

/**
 * "Came from": the roster uploads, batch extractions and manual entries this prospect came from,
 * newest first, for system admins only. With none recorded, it falls back to when (and by whom) the
 * record was created.
 *
 * The role check happens before anything that fetches is mounted, because the endpoint 403s every
 * other role and the request should not be made at all. It reads the context directly rather than
 * through `useAuth`, which throws outside a provider, so the prospect viewer still renders in a
 * tree without one (and shows nothing here).
 */
export const ProspectCameFrom = ({ prospectId }: { prospectId: string }) => {
  const isSystemAdmin = useContext(AuthContext)?.isSystemAdmin === true;
  if (!isSystemAdmin) return null;

  return (
    <section className="prospect-came-from" aria-labelledby="prospect-came-from-title">
      <h4 className="prospect-came-from__heading-title" id="prospect-came-from-title">
        Came from
        <span className="prospect-came-from__admin-note"> (system admin)</span>
      </h4>
      {/* Keyed so another prospect starts from a clean loading state. */}
      <ProspectSourcesList key={prospectId} prospectId={prospectId} />
    </section>
  );
};
